import { Dialog, DialogContent, DialogProps, DialogTitle } from '@material-ui/core';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { ExtendedRadioGroup } from 'framework/components/ExtendedRadioGroup';
import { CancelSubmitFormDialogActions } from 'framework/forms/CancelSubmitFormDialogActions';
import { FormDatePicker } from 'framework/forms/FormDatePicker';
import { FormTextField } from 'framework/forms/FormTextField';
import { handleFormResponse } from 'framework/forms/utils/handleFormResponse';
import { setFormValue } from 'framework/forms/utils/setFormValue';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { IRegisterInventoryItemAsSoldRequest, inventoryItemsCommand_registerAsSold } from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import React, { useState } from 'react';
import { FormSelectPatientField } from 'shared/patientSearch/FormSelectPatientField';
import { PatientSearchProvider } from 'shared/patientSearch/PatientSearchProvider';
import { useNow } from 'shared/utils/useNow';
import * as yup from 'yup';

const createSchema = (strings: IStrings) => {
	return yup.object<IRegisterInventoryItemAsSoldRequest>({
		reference: yup.string().required(),
		referenceDate: yup.date().required(),
		patientId: yup.string().required(),
		inventoryItemId: yup.string().required(),
	});
};

interface IProps extends DialogProps {
	inventoryItemId: string;
	patient?: { identifier: string; id: string };
	confirm: VoidFunction;
	cancel: VoidFunction;
}

type PatientMode = 'this' | 'select';

export const InventoryItemRegisterAsSoldForm = ({ confirm, cancel, inventoryItemId, patient, ...rest }: IProps) => {
	const strings = useLocalization();
	const [submit, isSubmitting] = useFormSubmit(inventoryItemsCommand_registerAsSold);
	const now = useNow();

	const handleSubmit = async (values: IRegisterInventoryItemAsSoldRequest, helpers: FormikHelpers<IRegisterInventoryItemAsSoldRequest>) => {
		// const rValues = patient === undefined || patientMode === 'select' ? values : { ...values, patientId: patient.id };
		const r = await submit(values);
		if (handleFormResponse(r, helpers)) {
			confirm();
		}
	};

	if (now === undefined) {
		return <div></div>;
	}

	return (
		<Formik<IRegisterInventoryItemAsSoldRequest>
			validateOnMount
			initialValues={{
				reference: '',
				referenceDate: now,
				patientId: patient?.id ?? '',
				inventoryItemId: inventoryItemId,
			}}
			validationSchema={createSchema(strings)}
			onSubmit={handleSubmit}>
			<Inner
				patient={patient}
				isSubmitting={isSubmitting}
				cancel={cancel}
				{...rest}
			/>
		</Formik>
	);
};

type InnerProps = DialogProps & {
	patient?: { identifier: string; id: string };
	isSubmitting: boolean;
	cancel: VoidFunction;
};

const Inner = ({ patient, isSubmitting, cancel, ...rest }: InnerProps) => {
	const strings = useLocalization();
	const [patientMode, setPatientMode] = useState<PatientMode>(patient === undefined ? 'select' : 'this');
	const props = useFormikContext<IRegisterInventoryItemAsSoldRequest>();

	const onSetPatientMode = (val: PatientMode) => {
		setPatientMode(val);
		setFormValue<IRegisterInventoryItemAsSoldRequest>('patientId', val === 'this' ? patient!.id : '', props);
	};

	return (
		<Form>
			<PatientSearchProvider>
				<Dialog
					fullWidth
					{...rest}>
					<DialogTitle>{strings.registerInventoryItemAsSold}</DialogTitle>
					<DialogContent
						className='df-col'
						dividers>
						{patient && (
							<ExtendedRadioGroup<PatientMode>
								selected={patientMode}
								setSelected={onSetPatientMode}
								options={[
									{ value: 'this', label: `${strings.forPatient}: ${patient.identifier}` },
									{ value: 'select', label: `${strings.selectAnotherPatient}` },
								]}
							/>
						)}
						{patientMode === 'select' && (
							<FormSelectPatientField<IRegisterInventoryItemAsSoldRequest>
								name='patientId'
								label={strings.patient}
							/>
						)}
						<FormTextField<IRegisterInventoryItemAsSoldRequest>
							name='reference'
							label={strings.reference}
						/>
						<FormDatePicker<IRegisterInventoryItemAsSoldRequest>
							name='referenceDate'
							label={strings.referenceDate}
						/>
					</DialogContent>
					<CancelSubmitFormDialogActions
						cancel={cancel}
						isSubmitting={isSubmitting}
						submitText={strings.add}
					/>
				</Dialog>
			</PatientSearchProvider>
		</Form>
	);
};
